import { CameraController } from './cameraController';
import * as THREE from 'three';

/**
 * 相机同步管理器 - 单例模式
 * 负责管理多个相机控制器的同步
 */
export class CameraSyncManager {
    private static instance: CameraSyncManager | null = null;
    private cameraControllers: CameraController[] = [];
    private isEnabled: boolean = true;
    private isSyncing: boolean = false;
    private masterCamera: CameraController | null = null;

    private constructor() {}

    /**
     * 获取单例实例
     */
    public static getInstance(): CameraSyncManager {
        if (!CameraSyncManager.instance) {
            CameraSyncManager.instance = new CameraSyncManager();
        }
        return CameraSyncManager.instance;
    }

    /**
     * 注册相机控制器
     */
    public registerCamera(cameraController: CameraController): void {
        if (!this.cameraControllers.includes(cameraController)) {
            this.cameraControllers.push(cameraController);
            
            // 如果这是第一个注册的相机，设为主相机
            if (this.cameraControllers.length === 1) {
                this.masterCamera = cameraController;
            }
        }
    }

    /**
     * 注销相机控制器
     */
    public unregisterCamera(cameraController: CameraController): void {
        const index = this.cameraControllers.indexOf(cameraController);
        if (index > -1) {
            this.cameraControllers.splice(index, 1);
            
            // 如果注销的是主相机，重新选择主相机
            if (cameraController === this.masterCamera) {
                this.masterCamera = this.cameraControllers.length > 0 ? this.cameraControllers[0] : null;
            }
        }
    }

    /**
     * 设置主相机（用于同步的源相机）
     */
    public setMasterCamera(cameraController: CameraController): void {
        if (this.cameraControllers.includes(cameraController)) {
            this.masterCamera = cameraController;
        }
    }

    /**
     * 获取主相机
     */
    public getMasterCamera(): CameraController | null {
        return this.masterCamera;
    }

    /**
     * 启用/禁用同步
     */
    public setEnabled(enabled: boolean): void {
        this.isEnabled = enabled;
    }

    /**
     * 检查是否启用同步
     */
    public isSyncEnabled(): boolean {
        return this.isEnabled;
    }

    /**
     * 同步所有相机到主相机状态
     * 这个方法由主相机的 updateCameraTransform 调用
     */
    public syncCameras(sourceCameraController: CameraController): void {
        if (!this.isEnabled || this.isSyncing || this.cameraControllers.length < 2) {
            return;
        }

        this.isSyncing = true;
        try {
            const sourceCamera = sourceCameraController.getCamera();
            if (!sourceCamera) return;

            // 同步到所有其他相机
            for (const cameraController of this.cameraControllers) {
                if (cameraController !== sourceCameraController) {
                    cameraController.applyCameraState(sourceCamera);
                }
            }
        } finally {
            this.isSyncing = false;
        }
    }

    /**
     * 获取所有注册的相机控制器
     */
    public getRegisteredCameras(): CameraController[] {
        return [...this.cameraControllers];
    }

    /**
     * 清空所有注册的相机
     */
    public clear(): void {
        this.cameraControllers = [];
        this.masterCamera = null;
        console.log('所有相机控制器已清空');
    }
}

// 导出单例实例
export const cameraSyncManager = CameraSyncManager.getInstance();
